import React, { useContext, useEffect, useRef, useState } from "react";
import { ThemeConsumer } from "styled-components";

import AnswerSlide from "@/common/AnswerSlide";
import { CopyrightOverlay } from "@/common/CopyrightOverlay";
import { Footer } from "@/common/Footer";
import { Intro } from "@/common/Intro";
import { renderOutro } from "@/common/Outro";
import Score from "@/common/Score";
import Slider, { SlideProps, SliderActions } from "@/common/Slider";
import Header from "@/components/Header";
import { SharingType } from "@/components/Sharing";
import { Subline } from "@/components/Subline";
import { Group, Main, Stage, Wrapper } from "@/containers/styles";
import {
  GlobalContext,
  QuizConsumer,
  QuizContextProps,
  QuizProvider,
} from "@/contexts";
import LocaleContext from "@/i18n";
import EventSystem from "@/service/EventSystem";
import IFrameService from "@/service/IFrame";
import InteractionService from "@/service/Interaction";
import {
  InteractionIntro,
  InteractionScore,
  InteractionScoreResult,
  Option,
  OutroType,
  Quiz,
  QuizResult,
  QuizResultsContainer,
  QuizVote,
  Slide,
} from "@/service/Model";
import { getSharingTitle, handleShare } from "@/utils/socialActions";
import { isExpired, isFuture } from "@/utils/time";

// Localized components
import { QuizBody } from "./QuizBody";
import { QuizOptions } from "./QuizOptions";
import { QuizSummary } from "./QuizSummary";

type QuizContainerProps = {
  interaction: Quiz;
  interactionResults: QuizResultsContainer;
};

type QuizInfo = {
  title: string;
  text: string;
};

type Overlay = {
  closable: boolean;
  message: string;
  autoFadeOut: boolean;
};

const INTRO_SLIDE_KEY = "intro";
const SUMMARY_SLIDE_KEY = "summary";
const SUMMARY_SCORE_SLIDE_KEY = "summary_score";

const QuizContainer: React.FC<QuizContainerProps> = (
  props: QuizContainerProps
): React.JSX.Element => {
  const globalContext = useContext(GlobalContext);
  const localeContext = useContext(LocaleContext);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const laHyperScrollFinishedRef = useRef<boolean>(false);
  const retryRef = useRef<boolean>(false);
  const nextBranchSlideIdRef = useRef<string | undefined>(undefined);

  const { interaction, interactionResults } = props;

  const [showOutro, setShowOutro] = useState<boolean>(false);
  const [title, setTitle] = useState<string>("");
  const [showTitle, setShowTitle] = useState<boolean>(false);
  const [navigation, setNavigation] = useState<{
    previous: boolean;
    next: boolean;
  }>({
    previous: true,
    next: true,
  });
  const [footNote, setFootNote] = useState<string>("");
  const [overlay, setOverlay] = useState<Overlay | undefined>(undefined);
  const [showImageCopyrights, setShowImageCopyrights] =
    useState<boolean>(false);

  const imagesData = InteractionService.getImagesData(interaction);
  const expired = !!(interaction.endDate && interaction.endDate < Date.now());

  useEffect(() => {
    // Register on container when scroll transition ended
    IFrameService.hasScrolledListener();

    // This event is triggered when la-hyper finishes scrolling
    EventSystem.subscribe("parent:scroll:finished", () => {
      laHyperScrollFinishedRef.current = true;
      if (retryRef.current) {
        // Prevent automatic slide transition after retry
        retryRef.current = false;
      } else {
        setTimeout(() => changeSlide(nextBranchSlideIdRef.current), 300);
      }
    });
  }, []);

  const convertSlideIdToSlideIndex = (slideId: string) =>
    slides.findIndex((slide: SlideProps) => slide.key.includes(slideId));

  const changeSlide = (branch?: string): void => {
    if (branch) {
      const slideIndex = convertSlideIdToSlideIndex(branch);
      handleCustomSlideChange(SliderActions.goto, slideIndex);
    } else {
      handleCustomSlideChange(SliderActions.next);
    }
  };

  const handleNextSlide = (delay: number, branch?: string): void => {
    // This ref is used after scroll:finished event received (see useEffect)
    nextBranchSlideIdRef.current = branch;
    setTimeout(() => {
      if (globalContext.hyperAvailable) {
        laHyperScrollFinishedRef.current = false;
        IFrameService.scroll();
        // la-hyper scroll:finished event received? (see useEffect)
        // If not slide manually after timeout
        setTimeout(() => {
          if (!laHyperScrollFinishedRef.current) {
            laHyperScrollFinishedRef.current = true;
            changeSlide(branch);
          }
        }, 2000);
      } else {
        changeSlide(branch);
      }
    }, delay);
  };

  const handleRetryQuiz = (): void => {
    if (globalContext.hyperAvailable) {
      nextBranchSlideIdRef.current = undefined;
      retryRef.current = true;
      IFrameService.scroll();
    }
    handleCustomSlideChange(SliderActions.goto, 0);
  };

  const handleShareClick = (type: SharingType): void => {
    const sharingTitle = getSharingTitle(interaction);
    handleShare(type, sharingTitle, globalContext?.brandSettings?.xHandle);
  };

  const hasAnswerSlide = (id: string): boolean =>
    interaction.slides.some(
      (slide: Slide) =>
        slide.id === id &&
        slide.options.find(
          (option: Option) => option.answer_text || option.answer_image
        )
    );

  const getSliderPosition = (votedSlideIds: string[]): number => {
    const answerSlideOffset = votedSlideIds.filter((id: string) =>
      hasAnswerSlide(id)
    ).length;
    const slideCount = votedSlideIds.length + answerSlideOffset;

    if (!expired && slideCount) {
      return interaction.intro ? slideCount + 1 : slideCount;
    }
    return 0;
  };

  const generateSlides = (
    votes: QuizVote[],
    correctAnswersCount: number,
    deleteLocalAnswers: () => void
  ): SlideProps[] => {
    const closedQuizSlide = generateClosedQuizSlide(
      interaction.startDate,
      interaction.endDate,
      interaction.outro?.outroType === OutroType.RegistrationForm
    );
    if (closedQuizSlide) {
      return [closedQuizSlide];
    }

    let slideProps: SlideProps[] = [];

    // Add Quiz Intro
    if (interaction.intro) {
      slideProps.push(generateIntroSlide(interaction.intro));
    }

    // Add Quiz Slides
    slideProps = [...slideProps, ...generateQuizSlides(votes)];

    // Add Conditional Score if present on interaction:
    const lastSlide = interaction.score
      ? generateScoreSlide(
          interaction.score,
          correctAnswersCount,
          deleteLocalAnswers
        )
      : generateSummarySlide(correctAnswersCount, deleteLocalAnswers);

    slideProps.push(lastSlide);

    return slideProps;
  };

  const getVotedSlideIds = (votes: QuizVote[]): string[] =>
    interaction.slides
      .filter((slide: Slide) =>
        votes.find((vote: QuizVote) => vote.slideId === slide.id)
      )
      .map((slide: Slide) => slide.id);

  const quizCompleted = (votedSlideIds: string[]): boolean =>
    votedSlideIds.length === interaction.slides.length;

  const handleCustomSlideChange = (
    direction: SliderActions,
    slideIndex?: number
  ) => {
    IFrameService.reportAnswerAction();
    EventSystem.publish(`slider:action:${direction}`, slideIndex);
  };

  const handleSlideChange = (
    nextSlideId: string,
    nextSlideTitle: string,
    hasPrevious: boolean,
    hasNext: boolean
  ): void => {
    const { translation } = localeContext;
    const totalQuestionSlides = interaction.slides.length;
    const questionSlideIndex: number = interaction.slides.findIndex(
      (slide: Slide) => nextSlideId.startsWith(slide.id)
    );

    setTitle(nextSlideTitle);
    setShowTitle(true);
    setNavigation({ previous: hasPrevious, next: hasNext });
    setFootNote(`${questionSlideIndex + 1}/${totalQuestionSlides}`);

    switch (nextSlideId) {
      case INTRO_SLIDE_KEY:
        setFootNote("");
        break;
      case SUMMARY_SLIDE_KEY:
      case SUMMARY_SCORE_SLIDE_KEY:
        setFootNote(translation.result);
        setTitle(interaction.evaluationText || translation.quizSummary.title);
        setOverlay({
          autoFadeOut: false,
          closable: false,
          message: translation.thankYou,
        });
        break;
      default:
        break;
    }
  };

  const renderSlider = (quizContext: QuizContextProps): React.JSX.Element => {
    const { votes } = quizContext;
    const votedSlideIds = getVotedSlideIds(votes);
    const stoppedAtSlide = getSliderPosition(votedSlideIds);
    const completed = quizCompleted(votedSlideIds);

    return (
      <ThemeConsumer>
        {(theme: ITheme) => {
          return (
            <Slider
              timing={500}
              selected={stoppedAtSlide}
              slides={slides}
              backgroundImageUrl={interaction.background}
              backgroundColor={
                interaction.backgroundColor || theme.colors.sliderBackground
              }
              isSlideVoted={(slideId: string) =>
                votedSlideIds.includes(slideId)
              }
              onSlideWillChange={(index: number) => {
                // fadeOut animations
                if (!slides[index]) {
                  return;
                }
                setShowTitle(false);
                setShowOutro(false);
                if (overlay) {
                  setOverlay({ ...overlay, autoFadeOut: true });
                }
              }}
              onSlideChanged={(index: number) => {
                // fadeIn animations
                if (!slides[index]) {
                  return;
                }
                const slide = slides[index];
                let hasPrevious = false,
                  hasNext = false;

                if (completed) {
                  hasPrevious = index > 0;
                  hasNext =
                    index < slides.length - 1 &&
                    (expired || votedSlideIds.includes(slide.key));
                }

                handleSlideChange(
                  slide.key,
                  slide.meta?.title || "",
                  hasPrevious,
                  hasNext
                );
              }}
              onEnd={() => {
                if (interaction.outro) {
                  setShowOutro(true);
                }
              }}
            />
          );
        }}
      </ThemeConsumer>
    );
  };

  const getFutureQuizInfo = (startDate: number, quizType: string): QuizInfo => {
    const { quizFutureIntro } = localeContext.translation;
    const date = new Date(startDate);
    return {
      text: quizFutureIntro.text(
        quizType,
        date.toLocaleDateString(),
        date.toLocaleTimeString()
      ),
      title: quizFutureIntro.title(quizType),
    };
  };

  const getExpiredQuizInfo = (endDate: number, quizType: string): QuizInfo => {
    const { quizEndedIntro } = localeContext.translation;
    const date = new Date(endDate);
    return {
      text: quizEndedIntro.text(
        quizType,
        date.toLocaleDateString(),
        date.toLocaleTimeString()
      ),
      title: quizEndedIntro.title(quizType),
    };
  };

  const generateClosedQuizSlide = (
    startDate: number | undefined,
    endDate: number | undefined,
    withRegistrationOutro: boolean
  ): SlideProps | void => {
    const { quiz, raffle } = localeContext.translation;
    const quizType = withRegistrationOutro ? raffle : quiz;

    if (startDate && isFuture(startDate)) {
      const quizInfo = getFutureQuizInfo(startDate, quizType);
      return {
        key: INTRO_SLIDE_KEY,
        meta: { title: quizInfo.title },
        children: <Intro text={quizInfo.text} label={""} hideButton={true} />,
      };
    }

    if (endDate && isExpired(endDate)) {
      const quizInfo = getExpiredQuizInfo(endDate, quizType);
      return {
        key: INTRO_SLIDE_KEY,
        meta: { title: quizInfo.title },
        children: <Intro text={quizInfo.text} label={""} hideButton={true} />,
      };
    }
  };

  const generateIntroSlide = (intro: InteractionIntro): SlideProps => {
    const backgroundImageUrl = intro.background || interaction.background;
    return {
      key: INTRO_SLIDE_KEY,
      meta: { title: intro.title },
      backgroundImageUrl,
      backgroundColor: interaction.backgroundColor,
      children: (
        <Intro
          text={intro.text}
          label={intro.label}
          onButtonClick={() => handleNextSlide(0)}
          hasBackgroundImage={!!backgroundImageUrl}
        />
      ),
    };
  };

  const generateQuizSlides = (votes: QuizVote[]): SlideProps[] => {
    const { results } = interactionResults;

    const getAnswerSlide = (slide: Slide): SlideProps => {
      const vote = votes.find((v: QuizVote) => v.slideId === slide.id);
      const answerText = slide.options.find(
        (o: Option) => o.id === vote?.optionId
      )?.answer_text;

      const answerImage = slide.options.find(
        (o: Option) => o.id === vote?.optionId
      )?.answer_image;

      return {
        key: `${slide.id}-answer`,
        meta: { title: slide.title },
        backgroundImageUrl: slide.background || interaction.backgroundColor,
        backgroundColor: interaction.backgroundColor,
        children: (
          <AnswerSlide
            isCorrect={vote?.answer}
            answerText={answerText}
            answerImage={answerImage}
            onButtonClick={() => handleNextSlide(0)}
          />
        ),
      };
    };

    const getQuizSlide = (slide: Slide): SlideProps => {
      const filteredResults: QuizResult[] = results.filter(
        (result: QuizResult) => result.slideId === slide.id
      );

      // Find option uuid in slide/question options (only single vote is possible)
      const userVote = votes.find((v: QuizVote) => slide.id === v.slideId);

      return {
        key: `${slide.id}`,
        meta: { title: slide.title },
        backgroundImageUrl: slide.background || interaction.backgroundColor,
        backgroundColor: interaction.backgroundColor,
        children: (
          <QuizBody questionImage={slide.questionImage}>
            <QuizOptions
              results={filteredResults}
              interactionId={interaction.id}
              showCorrectAnswer={interaction.showCorrectAnswer}
              slideId={slide.id}
              options={slide.options}
              userVote={userVote}
              layout={interaction.layout}
              onNext={(branch?: string) => handleNextSlide(2500, branch)}
            />
          </QuizBody>
        ),
      };
    };

    const slideProps: SlideProps[] = [];

    interaction.slides.forEach((slide: Slide) => {
      slideProps.push(getQuizSlide(slide));
      if (hasAnswerSlide(slide.id)) {
        slideProps.push(getAnswerSlide(slide));
      }
    });

    return slideProps;
  };

  const generateScoreSlide = (
    score: InteractionScore,
    correctAnswersCount: number,
    deleteLocalAnswers: () => void
  ): SlideProps => {
    // filter out all results which are higher then the count of the correct answers:
    const resultsAbove = score.result?.filter(
      (r: InteractionScoreResult) => correctAnswersCount >= r.range
    );

    // get the result which is closest to the count of the correct answers:
    const result = resultsAbove?.sort(
      (a: InteractionScoreResult, b: InteractionScoreResult) =>
        a.range - b.range
    )[resultsAbove.length - 1];

    const maxScoreReached = correctAnswersCount >= score.maxScore;

    return {
      key: SUMMARY_SCORE_SLIDE_KEY,
      backgroundImageUrl: interaction.background,
      backgroundColor: interaction.backgroundColor,
      children: (
        <Score
          range={`${correctAnswersCount}/${score.maxScore}`}
          text={result?.text}
          image={result?.image}
          icons={globalContext.icons}
          handleShareClick={handleShareClick}
          showRetry={!maxScoreReached}
          onLoad={() => setShowOutro(true)}
          onRetry={() => {
            deleteLocalAnswers();
            setOverlay(undefined);
            handleRetryQuiz();
          }}
        />
      ),
    };
  };

  const generateSummarySlide = (
    correctAnswersCount: number,
    deleteLocalAnswers: () => void
  ): SlideProps => {
    return {
      key: SUMMARY_SLIDE_KEY,
      backgroundImageUrl: interaction.background,
      backgroundColor: interaction.backgroundColor,
      children: (
        <QuizSummary
          icons={globalContext.icons}
          showDetails={interaction.slides.length === 1}
          userVoteCount={correctAnswersCount}
          totalVoteCount={interaction.slides.length}
          sharingTitle={getSharingTitle(interaction)}
          outroType={interaction.outro?.outroType}
          onLoad={() => setShowOutro(true)}
          onRetry={() => {
            deleteLocalAnswers();
            setOverlay(undefined);
            handleRetryQuiz();
          }}
        />
      ),
    };
  };

  const renderSliderOverlays = (): React.JSX.Element => {
    return (
      <React.Fragment>
        {overlay && (
          <Subline
            overlay={true}
            dismiss={overlay.autoFadeOut}
            showCloseButton={overlay.closable}
            onClose={() => setOverlay(undefined)}
          >
            {overlay.message}
          </Subline>
        )}
        {showImageCopyrights && (
          <CopyrightOverlay
            onClose={() => setShowImageCopyrights(false)}
            imagesData={imagesData}
          />
        )}
      </React.Fragment>
    );
  };

  const renderOutroOverlay = (
    quizContext: QuizContextProps
  ): React.JSX.Element | null => {
    const outro = props.interaction.outro;

    if (
      !outro ||
      !showOutro ||
      (outro.showOnlyForCorrectAnswers && !quizContext.hasAllAnswersCorrect)
    ) {
      return null;
    }

    return renderOutro(outro, { globals: globalContext }, () =>
      setShowOutro(!showOutro)
    );
  };

  const renderQuiz = (quizContext: QuizContextProps): React.JSX.Element => {
    const { participantsCount } = interactionResults;
    const { votes } = quizContext;

    const voted = votes.length > 0;
    const participantsCountPlusVote = voted
      ? participantsCount + 1
      : participantsCount;

    return (
      <Wrapper ref={wrapperRef}>
        <Header
          icons={globalContext.icons}
          visible={showTitle}
          navigation={navigation}
          onNavigationClick={(
            e: React.MouseEvent,
            direction: SliderActions
          ) => {
            handleCustomSlideChange(direction);
          }}
        >
          {title}
        </Header>
        <Stage>
          <Group>
            <Main>
              {renderSlider(quizContext)}
              {renderSliderOverlays()}
            </Main>
            <Footer
              participants={participantsCountPlusVote}
              hideParticipantsCount={interaction.hideParticipantsCount}
              leftText={footNote}
              onShareClick={handleShareClick}
              onCopyrightClick={() => setShowImageCopyrights(true)}
              hasCopyrights={imagesData.length > 0}
            />
          </Group>
        </Stage>
      </Wrapper>
    );
  };

  let slides: SlideProps[] = [];

  return (
    <QuizProvider
      quizId={interaction.id}
      slides={interaction.slides}
      preview={!!globalContext.query.preview}
    >
      <QuizConsumer>
        {(quizContext: QuizContextProps) => {
          slides = generateSlides(
            quizContext.votes,
            quizContext.correctAnswersCount,
            quizContext.resetVotes
          );

          return (
            <React.Fragment>
              {renderQuiz(quizContext)}
              {renderOutroOverlay(quizContext)}
            </React.Fragment>
          );
        }}
      </QuizConsumer>
    </QuizProvider>
  );
};

export default QuizContainer;
export { QuizContainer };
